/* eslint-disable no-console */
import React, { useCallback, useEffect, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { useAuthContext } from "../../context/auth";
import { getOportunidades, getOportunidadesBTL } from "../../utils";
import { calculateSalesSums, normalizeMonth, calculateForecast } from "../../utils/calculateVentas";
import "./stylesVentasTabla.scss";

export const VentasTabla = ({ filtroAno, setFiltroAno, showInDollars, setShowInDollars }) => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const [oportunidades, setOportunidades] = useState([]);
  const [oportunidadesBTL, setOportunidadesBTL] = useState([]);
  const [, setOportunidades2024] = useState([]);
  const possibleYears = ["2024", "2025", "2026"];
  const { pais } = useAuthContext();

  const localString = useCallback(
    (pais) =>
      ({
        Chile: "es-CL",
        Perú: "es-PE",
      })[pais] || "es-CL",
    []
  );

  const moneda = useCallback(
    (pais) =>
      ({
        Chile: "$ ",
        Perú: "S/ ",
      })[pais] || "$ ",
    []
  );

  const tiposDeCambio = {
    Perú: 3.75,
    Chile: 950,
  };
  const rate = tiposDeCambio[pais] || 1;

  const getMoneda = useCallback(() => (showInDollars ? "US$ " : moneda(pais)), [showInDollars, pais]);
  const getLocale = useCallback(() => (showInDollars ? "en-US" : localString(pais)), [showInDollars, pais]);

  const getDisplayValue = useCallback(
    (value) => {
      const convertedValue = showInDollars ? value / rate : value;
      const locale = showInDollars ? (pais === "Chile" ? "es-CL" : "en-US") : localString(pais);
      return convertedValue.toLocaleString(locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        ...(pais === "Chile" && showInDollars ? { useGrouping: true, thousandSeparator: "." } : {}),
      });
    },
    [showInDollars, rate, pais]
  );

  const handleAnoChange = (event) => {
    setFiltroAno(event.target.value);
  };

  const objetivosPorMes2024 = useCallback((pais) => {
    let objetivos = [];
    if (pais === "Chile") {
      objetivos = [
        5000000, 7500000, 10000000, 15000000, 15000000, 15000000, 15000000, 15000000, 15000000, 25000000, 35000000,
        40000000,
      ];
    } else if (pais === "Perú") {
      objetivos = [90418, 137130, 141569, 128100, 165900, 185900, 185780, 215600, 237000, 221340, 226940, 230300];
    }
    return objetivos;
  }, []);

  const objetivosPorMes2025 = useCallback((pais) => {
    let objetivos = [];
    if (pais === "Chile") {
      objetivos = [
        13120060,
        6560030,
        18368084 + 2755213,
        34329394 + 8582349,
        21125781 + 5281445,
        30368310 + 7592078,
        35228630 + 17614315,
        28823424 + 14411712,
        32026027 + 16013014,
        46966329 + 23483164,
        30192640 + 15096320,
        16773689 + 8386844,
      ];
    } else if (pais === "Perú") {
      objetivos = [60000, 70000, 80000, 80000, 100000, 100000, 120000, 130000, 140000, 140000, 140000, 140000];
    }
    return objetivos;
  }, []);

  const objetivosPorMes2026 = useCallback((pais) => {
    let objetivos = [];
    if (pais === "Chile") {
      objetivos = [
        18000000, 20000000, 22000000, 24000000, 26000000, 28000000, 30000000, 32000000, 34000000, 36000000, 38000000,
        40000000,
      ];
    } else if (pais === "Perú") {
      objetivos = [150000, 160000, 170000, 180000, 190000, 200000, 210000, 220000, 230000, 240000, 250000, 260000];
    }
    return objetivos;
  }, []);

  const objetivosPorMes = useCallback(
    (pais, ano) => {
      if (ano === "2024") return objetivosPorMes2024(pais);
      if (ano === "2025") return objetivosPorMes2025(pais);
      if (ano === "2026") return objetivosPorMes2026(pais);
      return [];
    },
    [objetivosPorMes2024, objetivosPorMes2025, objetivosPorMes2026]
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const allOportunidades = await getOportunidades({ pais });
        const filteredOportunidades = allOportunidades.filter((oportunidad) => {
          const hasMesFactura = !!oportunidad.mes_factura || !!oportunidad.facturafecha;
          const hasMesCierre = !!oportunidad.mes_cierre;
          console.log(
            `Oportunidad ID ${oportunidad.id}: mes_factura=${oportunidad.mes_factura}, mes_cierre=${oportunidad.mes_cierre}, pais=${oportunidad.pais}`
          );
          return hasMesFactura && hasMesCierre && oportunidad.pais === pais;
        });
        setOportunidades(filteredOportunidades);
        const allOportunidadesBTL = await getOportunidadesBTL({ pais });
        const filteredOportunidadesBTL = allOportunidadesBTL
          .map((btl) => ({
            ...btl,
            mes_cierre: normalizeMonth(btl.mes_cierre, btl.created_at, btl.ano),
            mes_factura: normalizeMonth(btl.mes_factura, btl.created_at, btl.ano),
          }))
          .filter((btl) => {
            const hasMesFactura = !!btl.mes_factura;
            const hasMesCierre = !!btl.mes_cierre;
            console.log(
              `BTL ID ${btl.id}: mes_factura=${btl.mes_factura}, mes_cierre=${btl.mes_cierre}, pais=${btl.pais}`
            );
            return hasMesFactura && hasMesCierre && btl.pais === pais;
          });
        setOportunidadesBTL(filteredOportunidadesBTL);
        if (filtroAno === "2025") {
          const oportunidades2024 = allOportunidades.filter((oportunidad) => {
            const year = parseInt(oportunidad.mes_factura.split("-")[1], 10);
            return year === 2024 && oportunidad.pais === pais;
          });
          setOportunidades2024(oportunidades2024);
        } else {
          setOportunidades2024([]);
        }
      } catch (error) {
        console.error("Error in fetchData:", error);
      }
    };
    fetchData();
  }, [pais, filtroAno]);

  const getMonthName = (index) => {
    const months = ["ENE", "FEB", "MAR", "ABR", "MAY", "JUN", "JUL", "AGO", "SEP", "OCT", "NOV", "DIC"];
    return months[index];
  };

  const getMonthSums = (monthIndex) => {
    const startMonth = monthIndex + 1;
    const endMonth = monthIndex + 1;
    return calculateSalesSums(
      oportunidades,
      oportunidadesBTL,
      filtroAno || currentYear,
      startMonth,
      endMonth,
      rate,
      pais
    );
  };

  const getQuarterSums = (quarterIndex) => {
    const startMonth = quarterIndex === 1 ? 1 : quarterIndex === 2 ? 4 : quarterIndex === 3 ? 7 : 10;
    const endMonth = quarterIndex === 1 ? 3 : quarterIndex === 2 ? 6 : quarterIndex === 3 ? 9 : 12;
    return calculateSalesSums(
      oportunidades,
      oportunidadesBTL,
      filtroAno || currentYear,
      startMonth,
      endMonth,
      rate,
      pais
    );
  };

  const getHalfSums = (halfIndex) => {
    const startMonth = halfIndex === 1 ? 1 : 7;
    const endMonth = halfIndex === 1 ? 6 : 12;
    return calculateSalesSums(
      oportunidades,
      oportunidadesBTL,
      filtroAno || currentYear,
      startMonth,
      endMonth,
      rate,
      pais
    );
  };

  const yearlySums = calculateSalesSums(oportunidades, oportunidadesBTL, filtroAno || currentYear, 1, 12, rate, pais);

  const renderSubtotal = (subtotal) => (
    <td className="text-end">
      {getMoneda()}
      {getDisplayValue(subtotal)}
    </td>
  );

  const renderForecastMensual = (monthIndex) => {
    const { sumaImportes90, sumaImportes75 } = getMonthSums(monthIndex);
    const forecast = calculateForecast(sumaImportes90, sumaImportes75);
    return (
      <td className="text-end">
        {getMoneda()}
        {getDisplayValue(forecast)}
      </td>
    );
  };

  const renderCumplimiento = (index) => (
    <td className="text-center col-1">
      {((getMonthSums(index).sumaImportes100 / objetivosPorMes(pais, filtroAno)[index]) * 100).toLocaleString(
        getLocale(),
        {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }
      )}
      %
    </td>
  );

  const totalYTD = objetivosPorMes(pais, filtroAno).reduce((total, monto) => total + monto, 0);
  const totalQ1 = objetivosPorMes(pais, filtroAno)
    .slice(0, 3)
    .reduce((total, monto) => total + monto, 0);
  const totalQ2 = objetivosPorMes(pais, filtroAno)
    .slice(3, 6)
    .reduce((total, monto) => total + monto, 0);
  const totalQ3 = objetivosPorMes(pais, filtroAno)
    .slice(6, 9)
    .reduce((total, monto) => total + monto, 0);
  const totalQ4 = objetivosPorMes(pais, filtroAno)
    .slice(9, 12)
    .reduce((total, monto) => total + monto, 0);
  const totalH1 = totalQ1 + totalQ2;
  const totalH2 = totalQ3 + totalQ4;

  return (
    <div style={{ backgroundColor: "#E0E0E0", padding: "10px", borderRadius: "6px" }}>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <Dropdown className="m-0">
          <Dropdown.Toggle variant="white border" id="dropdown-basic" className="form-control text-start">
            {filtroAno || "Filtrar por año"}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {possibleYears.map((ano, index) => (
              <div key={index} className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="anoSelection"
                  id={`ano${index}`}
                  value={ano}
                  checked={filtroAno === ano}
                  onChange={handleAnoChange}
                  style={{ marginLeft: "-15px" }}
                />
                <label className="form-check-label" htmlFor={`ano${index}`} style={{ marginLeft: "10px" }}>
                  {ano}
                </label>
              </div>
            ))}
          </Dropdown.Menu>
        </Dropdown>
        <div className="d-flex align-items-center justify-content-end">
          <input
            type="checkbox"
            id="verDolares"
            checked={showInDollars}
            onChange={(e) => setShowInDollars(e.target.checked)}
            className="me-2"
          />
          <label htmlFor="verDolares">Ver en dólares</label>
        </div>
      </div>
      <div className="d-flex justify-content-center mt-2">
        <table className="table-ventas table table-striped table-hover table-bordered table-sm">
          <thead className="text-center align-middle">
            <tr>
              <th style={{ width: "5%" }}>MES</th>
              <th style={{ width: "8%" }} className="bg-primary text-white">
                CIERRES
              </th>
              <th style={{ width: "8%" }} className="bg-success text-white">
                FACTURACIÓN
              </th>
              <th style={{ width: "8%" }}>OBJETIVO</th>
              <th style={{ width: "8%" }}>CUOTA</th>
              <th style={{ width: "8%" }} className="bg-danger text-white">
                90%
              </th>
              <th style={{ width: "8%" }} className="bg-primary text-white">
                75%
              </th>
              <th style={{ width: "8%" }} className="bg-warning text-white">
                50%
              </th>
              <th style={{ width: "8%" }} className="bg-info text-white">
                FORECAST
              </th>
            </tr>
          </thead>
          <tbody>
            {[...Array(12)].map((_, index) => {
              const { sumCierres, sumaImportes100, sumaImportes90, sumaImportes75, sumaImportes50 } =
                getMonthSums(index);
              return (
                <tr key={index}>
                  <th scope="row">{getMonthName(index)}</th>
                  {renderSubtotal(sumCierres)}
                  {renderSubtotal(sumaImportes100)}
                  <td className="text-end">
                    {getMoneda()}
                    {getDisplayValue(objetivosPorMes(pais, filtroAno)[index])}
                  </td>
                  {renderCumplimiento(index)}
                  {renderSubtotal(sumaImportes90)}
                  {renderSubtotal(sumaImportes75)}
                  {renderSubtotal(sumaImportes50)}
                  {renderForecastMensual(index)}
                </tr>
              );
            })}
            <tr className="table-dark">
              <th scope="row" className="text-center">
                TOTAL
              </th>
              {renderSubtotal(yearlySums.sumCierres)}
              {renderSubtotal(yearlySums.sumaImportes100)}
              <td className="text-end">
                {getMoneda()}
                {getDisplayValue(totalYTD)}
              </td>
              <td className="text-center">
                {((yearlySums.sumaImportes100 / totalYTD) * 100).toLocaleString(getLocale(), {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
                %
              </td>
              {renderSubtotal(yearlySums.sumaImportes90)}
              {renderSubtotal(yearlySums.sumaImportes75)}
              {renderSubtotal(yearlySums.sumaImportes50)}
              <td className="text-end">
                {getMoneda()}
                {getDisplayValue(calculateForecast(yearlySums.sumaImportes90, yearlySums.sumaImportes75))}
              </td>
            </tr>
            <tr>
              <th scope="row" className="text-center">
                Q1
              </th>
              {renderSubtotal(getQuarterSums(1).sumCierres)}
              {renderSubtotal(getQuarterSums(1).sumaImportes100)}
              <td className="text-end">
                {getMoneda()}
                {getDisplayValue(totalQ1)}
              </td>
              <td className="text-center">
                {((getQuarterSums(1).sumaImportes100 / totalQ1) * 100).toLocaleString(getLocale(), {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
                %
              </td>
              {renderSubtotal(getQuarterSums(1).sumaImportes90)}
              {renderSubtotal(getQuarterSums(1).sumaImportes75)}
              {renderSubtotal(getQuarterSums(1).sumaImportes50)}
              <td className="text-end">
                {getMoneda()}
                {getDisplayValue(calculateForecast(getQuarterSums(1).sumaImportes90, getQuarterSums(1).sumaImportes75))}
              </td>
            </tr>
            <tr>
              <th scope="row" className="text-center">
                Q2
              </th>
              {renderSubtotal(getQuarterSums(2).sumCierres)}
              {renderSubtotal(getQuarterSums(2).sumaImportes100)}
              <td className="text-end">
                {getMoneda()}
                {getDisplayValue(totalQ2)}
              </td>
              <td className="text-center">
                {((getQuarterSums(2).sumaImportes100 / totalQ2) * 100).toLocaleString(getLocale(), {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
                %
              </td>
              {renderSubtotal(getQuarterSums(2).sumaImportes90)}
              {renderSubtotal(getQuarterSums(2).sumaImportes75)}
              {renderSubtotal(getQuarterSums(2).sumaImportes50)}
              <td className="text-end">
                {getMoneda()}
                {getDisplayValue(calculateForecast(getQuarterSums(2).sumaImportes90, getQuarterSums(2).sumaImportes75))}
              </td>
            </tr>
            <tr>
              <th scope="row" className="text-center">
                Q3
              </th>
              {renderSubtotal(getQuarterSums(3).sumCierres)}
              {renderSubtotal(getQuarterSums(3).sumaImportes100)}
              <td className="text-end">
                {getMoneda()}
                {getDisplayValue(totalQ3)}
              </td>
              <td className="text-center">
                {((getQuarterSums(3).sumaImportes100 / totalQ3) * 100).toLocaleString(getLocale(), {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
                %
              </td>
              {renderSubtotal(getQuarterSums(3).sumaImportes90)}
              {renderSubtotal(getQuarterSums(3).sumaImportes75)}
              {renderSubtotal(getQuarterSums(3).sumaImportes50)}
              <td className="text-end">
                {getMoneda()}
                {getDisplayValue(calculateForecast(getQuarterSums(3).sumaImportes90, getQuarterSums(3).sumaImportes75))}
              </td>
            </tr>
            <tr>
              <th scope="row" className="text-center">
                Q4
              </th>
              {renderSubtotal(getQuarterSums(4).sumCierres)}
              {renderSubtotal(getQuarterSums(4).sumaImportes100)}
              <td className="text-end">
                {getMoneda()}
                {getDisplayValue(totalQ4)}
              </td>
              <td className="text-center">
                {((getQuarterSums(4).sumaImportes100 / totalQ4) * 100).toLocaleString(getLocale(), {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
                %
              </td>
              {renderSubtotal(getQuarterSums(4).sumaImportes90)}
              {renderSubtotal(getQuarterSums(4).sumaImportes75)}
              {renderSubtotal(getQuarterSums(4).sumaImportes50)}
              <td className="text-end">
                {getMoneda()}
                {getDisplayValue(calculateForecast(getQuarterSums(4).sumaImportes90, getQuarterSums(4).sumaImportes75))}
              </td>
            </tr>
            <tr className="table-dark">
              <th scope="row" className="text-center">
                H1
              </th>
              {renderSubtotal(getHalfSums(1).sumCierres)}
              {renderSubtotal(getHalfSums(1).sumaImportes100)}
              <td className="text-end">
                {getMoneda()}
                {getDisplayValue(totalH1)}
              </td>
              <td className="text-center">
                {((getHalfSums(1).sumaImportes100 / totalH1) * 100).toLocaleString(getLocale(), {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
                %
              </td>
              {renderSubtotal(getHalfSums(1).sumaImportes90)}
              {renderSubtotal(getHalfSums(1).sumaImportes75)}
              {renderSubtotal(getHalfSums(1).sumaImportes50)}
              <td className="text-end">
                {getMoneda()}
                {getDisplayValue(calculateForecast(getHalfSums(1).sumaImportes90, getHalfSums(1).sumaImportes75))}
              </td>
            </tr>
            <tr className="table-dark">
              <th scope="row" className="text-center">
                H2
              </th>
              {renderSubtotal(getHalfSums(2).sumCierres)}
              {renderSubtotal(getHalfSums(2).sumaImportes100)}
              <td className="text-end">
                {getMoneda()}
                {getDisplayValue(totalH2)}
              </td>
              <td className="text-center">
                {((getHalfSums(2).sumaImportes100 / totalH2) * 100).toLocaleString(getLocale(), {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
                %
              </td>
              {renderSubtotal(getHalfSums(2).sumaImportes90)}
              {renderSubtotal(getHalfSums(2).sumaImportes75)}
              {renderSubtotal(getHalfSums(2).sumaImportes50)}
              <td className="text-end">
                {getMoneda()}
                {getDisplayValue(calculateForecast(getHalfSums(2).sumaImportes90, getHalfSums(2).sumaImportes75))}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};
